import { UniButton } from '@/design-system/components/buttons';
import { H6, TextBody, TextCaption } from '@/design-system/components/typography';
import { ScrollView, SxProp, View } from 'dripsy';
import _ from 'lodash';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/design-system/components/icons/Icon';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { CreateCardAccountStackParamList } from './type';
import moment from 'moment-timezone';
import { CardAccountTypeSelectFilters, CardCurrencySelectFilters, CardIdentityTypeSelectFilters } from '../../options';
import { formatAmountByDecimal } from '@/features/account-management/utils';

export interface CreateCardAccountProps {
  onClose: () => void;
  data: any;
  onLayout: (event: any) => void;
  returnData?: any;
  refresh?: () => void;
}

export const CreateCardAccountWebTwoScreen: React.FC<CreateCardAccountProps> = (props) => {
  const { onClose, data, onLayout, returnData, refresh } = props;
  const { t } = useTranslation('card-account');
  const layout = useContext(LayoutContext);
  const {
    topHeader,
    body,
    horizontalWrapper,
    textLabel,
    textDebitAmount,
    textTitle,
    textAtmPin,
    btn,
    textHeaderTitle,
    textHeaderSubTitle
  } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<CreateCardAccountStackParamList>>();

  const [instructionTime, setInstructionTime] = useState(moment().format('YYYY-MM-DD HH:mm:ss'));

  return (
    <View
      sx={{
        height: layout.isMobile ? '100%' : 'auto',
        width: layout.dimensions.width,
        maxWidth: layout.isMobile ? layout.dimensions.width : Math.min(500, layout.dimensions.width),
        maxHeight: layout.dimensions.height,
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <View sx={topHeader}>
        <Icon name="checkCircleBlue" height={90} width={90} />
        <H6 bold sx={textHeaderTitle}>
          {t('card-account::credit-card-new-account.new-account-placed.title--new-account-new-account-placed')}
        </H6>
        <TextCaption sx={textHeaderSubTitle}>
          {t('card-account::credit-card-new-account.new-account-placed.text--new-account-created')}
        </TextCaption>
      </View>
      <ScrollView sx={{ flex: 1, backgroundColor: 'transparent' }} nestedScrollEnabled>
        <View sx={body}>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('card-account::credit-card-new-account.glossary.account-name')}
            </TextCaption>
            <H6 sx={textAtmPin}>{data.name}</H6>
          </View>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('card-account::credit-card-new-account.glossary.mobile-number')}
            </TextCaption>
            <H6 sx={textAtmPin}>{`+${data.phoneCode} ${data.phoneNumber}`}</H6>
          </View>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('card-account::credit-card-new-account.glossary.account-type')}
            </TextCaption>
            <H6 sx={textAtmPin}>{CardIdentityTypeSelectFilters(t).find((el) => el.value === data.identityType)?.label}</H6>
          </View>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('card-account::credit-card-new-account.glossary.card-type')}
            </TextCaption>
            <H6 sx={textAtmPin}>{CardAccountTypeSelectFilters(t).find((el) => el.value === data.accountType)?.label}</H6>
          </View>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('card-account::credit-card-new-account.glossary.currency')}
            </TextCaption>
            <H6 sx={textAtmPin}>{CardCurrencySelectFilters(t).find((el) => el.value === data.currency)?.label}</H6>
          </View>
          {data.accountType == 'asset-link' && data.assetLink && (
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account::credit-card-new-account.glossary.account')}
              </TextCaption>
              <H6 sx={textAtmPin}>
                {data.assetLink.name} ({data.assetLink.accountNumber})
              </H6>
            </View>
          )}
          {data.accountType == 'fixed-limit' && data.creditAccount && (
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account::credit-card-new-account.glossary.credit-account')}
              </TextCaption>
              <H6 sx={textAtmPin}>
                {data.creditAccount.name} ({data.creditAccount.accountNumber})
              </H6>
            </View>
          )}
          {data.accountType == 'fixed-limit' && data.limit && (
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account::credit-card-new-account.glossary.limit')}
              </TextCaption>
              <H6 sx={textAtmPin}>
                {formatAmountByDecimal({ value: data.limit, decimals: 2 })} {data.currency}
              </H6>
            </View>
          )}
          {data.accountType == 'fixed-limit' && data.settlementAccount && (
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account::credit-card-new-account.glossary.settlement-account')}
              </TextCaption>
              <H6 sx={textAtmPin}>
                {data.settlementAccount.name} ({data.settlementAccount.accountNumber})
              </H6>
            </View>
          )}
          {data.individual && data?.identityType == 'individual' && (
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account::credit-card-new-account.glossary.account-holder')}
              </TextCaption>
              <H6 sx={textAtmPin}>{`${data.individual?.firstName} ${data.individual?.lastName}`}</H6>
            </View>
          )}
          <View sx={horizontalWrapper}>
            <TextBody sx={textLabel}>{t('card-account-management::lock-card.text--instruction-time')}</TextBody>
            <TextBody sx={textDebitAmount}>{returnData?.instructionTime || instructionTime || '-'}</TextBody>
          </View>
          <UniButton
            sx={btn}
            labelSx={{ color: '$balck' }}
            label={t('card-account-management::general.button--close')}
            onPress={() => {
              refresh && refresh();
              navigation.getParent()?.goBack();
            }}
          />
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  topHeader: {
    justifyContent: 'center',
    alignItems: 'center',
    background: 'primaryDefault',
    mt: '$10'
  },
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28,
    gap: '$5'
  },
  horizontalWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  textLabel: {
    color: '$textSecondary'
  },
  textDebitAmount: {
    mb: '$1',
    color: '$textPrimary'
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btn: {
    width: '100%',
    height: 44
  },
  textHeaderTitle: {
    mb: '$2'
  },
  textHeaderSubTitle: {
    color: '$textPrimary'
  }
};
